import { Component, Input, ViewChild, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { EnfafSelectLightComponent } from '../../../../components/enfaf-select-light/enfaf-select-light.component';
import { EnfafIconComponent } from '../../../../components/enfaf-icon/enfaf-icon.component';
import { PromotionSegmentationTypeOption } from '../promotions-form/promotion-form.models';
import { MultiAutocompleteLightComponent, SelectOption } from '../../../../components/multi-autocomplete-light/multi-autocomplete-light.component';
import { ApiService } from '../../../../core/services/api/api.service';
import { ComboStudent } from '../../../../models/interfaces/student.interface';
import { Course } from '../../../../models/interfaces/course.interface';
import { PromotionSegmentationType } from '../../../../models/enums/promotions/promotion_segmentation_type.enum';
import { PromotionDto } from '../../../../models/dto/promotion.dto';

@Component({
  selector: 'app-promotion-management',
  imports: [ReactiveFormsModule, EnfafSelectLightComponent, EnfafIconComponent, MultiAutocompleteLightComponent],
  templateUrl: './promotion-management.component.html',
  styleUrls: ['./promotion-management.component.scss']
})
export class PromotionManagementComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() segmentationControl?: FormControl<PromotionSegmentationType | null>;
  @Input() segmentationTypeOptions: PromotionSegmentationTypeOption[] = [];
  @ViewChild(MultiAutocompleteLightComponent) multiAutocomplete?: MultiAutocompleteLightComponent;
  @ViewChild('enrolledCoursesAutocomplete') enrolledCoursesAutocomplete?: MultiAutocompleteLightComponent;
  @ViewChild('notEnrolledCoursesAutocomplete') notEnrolledCoursesAutocomplete?: MultiAutocompleteLightComponent;

  PromotionSegmentationType = PromotionSegmentationType;
  selectedStudents: SelectOption[] = [];
  selectedEnrolledCourses: SelectOption[] = [];
  selectedNotEnrolledCourses: SelectOption[] = [];
  promotionSegmentationType = PromotionSegmentationType;

  // New flag to avoid automatic resets while loading the promotion
  private suppressSegmentationReset = false;

  private coursesCache: Course[] | null = null;
  private coursesFetchPromise: Promise<{ courses: Course[] }> | null = null;

  private segmentationSubscription?: Subscription;
  private syncInterval?: any;
  private isComponentDestroyed = false;

  constructor(
    private api: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input() set promotion(value: PromotionDto | null) {
    if (value) {
      this.loadPromotionSegmentation(value);
    }
  }

  ngOnInit() {
    if (this.segmentationControl) {
      this.segmentationSubscription = this.segmentationControl.valueChanges.subscribe((value) => {
        // If we are loading a promotion, we suppress the automatic reset
        if (this.suppressSegmentationReset) {
          return;
        }

        if (this.multiAutocomplete) {
          this.multiAutocomplete.resetSelection();
        }
        if (this.enrolledCoursesAutocomplete) {
          this.enrolledCoursesAutocomplete.resetSelection();
        }
        if (this.notEnrolledCoursesAutocomplete) {
          this.notEnrolledCoursesAutocomplete.resetSelection();
        }

        this.selectedStudents = [];
        this.selectedEnrolledCourses = [];
        this.selectedNotEnrolledCourses = [];

        // Do not clear the cache here, only reset the selections
        // this.coursesCache = null;
        // this.loadingCourses = false;
      });
    }
  }

  ngAfterViewInit() {
    this.syncInterval = setInterval(() => {
      if (this.segmentationControl?.value === PromotionSegmentationType.SPECIFIC_STUDENTS) {
        if (this.multiAutocomplete) {
          const currentSelected = this.multiAutocomplete.selected;
          if (JSON.stringify(this.selectedStudents) !== JSON.stringify(currentSelected)) {
            this.selectedStudents = [...currentSelected];
            this.cdr.detectChanges();
          }
        }
      } else if (this.segmentationControl?.value === PromotionSegmentationType.COURSE_ENROLLMENT) {
        if (this.enrolledCoursesAutocomplete) {
          const currentEnrolled = this.enrolledCoursesAutocomplete.selected;
          if (JSON.stringify(this.selectedEnrolledCourses) !== JSON.stringify(currentEnrolled)) {
            this.selectedEnrolledCourses = [...currentEnrolled];
            this.cdr.detectChanges();
          }
        }
        if (this.notEnrolledCoursesAutocomplete) {
          const currentNotEnrolled = this.notEnrolledCoursesAutocomplete.selected;
          if (JSON.stringify(this.selectedNotEnrolledCourses) !== JSON.stringify(currentNotEnrolled)) {
            this.selectedNotEnrolledCourses = [...currentNotEnrolled];
            this.cdr.detectChanges();
          }
        }
      } else {
        if (this.selectedStudents.length > 0 || this.selectedEnrolledCourses.length > 0 || this.selectedNotEnrolledCourses.length > 0) {
          this.selectedStudents = [];
          this.selectedEnrolledCourses = [];
          this.selectedNotEnrolledCourses = [];
          this.cdr.detectChanges();
        }
      }
    }, 200);
  }

  ngOnDestroy() {
    this.isComponentDestroyed = true;
    if (this.segmentationSubscription) {
      this.segmentationSubscription.unsubscribe();
    }
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
  }

  removeStudent(student: SelectOption) {
    if (this.multiAutocomplete) {
      this.multiAutocomplete.removeSelected(student);
      this.selectedStudents = this.multiAutocomplete.selected;
    }
  }

  removeCourse(course: SelectOption, type: 'enrolled' | 'notEnrolled') {
    if (type === 'enrolled' && this.enrolledCoursesAutocomplete) {
      this.enrolledCoursesAutocomplete.removeSelected(course);
      this.selectedEnrolledCourses = this.enrolledCoursesAutocomplete.selected;
    } else if (type === 'notEnrolled' && this.notEnrolledCoursesAutocomplete) {
      this.notEnrolledCoursesAutocomplete.removeSelected(course);
      this.selectedNotEnrolledCourses = this.notEnrolledCoursesAutocomplete.selected;
    }
  }

  onSegmentationChange(value: PromotionSegmentationType) {
    this.segmentationControl?.setValue(value);
  }

  fetchStudents = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      firstValueFrom(
        this.api.fetchReducedStudents({
          page: page + 1,
          limit: pageSize,
          search: search
        })
      )
        .then((response) => {
          const mappedData: SelectOption[] = response.data.map((student: ComboStudent) => ({
            id: student.id,
            title: student.fullName,
            subtitle: student.email,
            selected: false
          }));

          observer.next({
            data: mappedData,
            total: response.pagination.total
          });
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  };

  fetchCourses = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      this.getCourses()
        .then((courses) => {
          this.processCachedCourses(courses, search, page, pageSize, observer);
        })
        .catch((error) => {
          if (this.isComponentDestroyed) {
            observer.complete();
            return;
          }
          console.error('Error fetching courses:', error);
          observer.error(error);
        });
    });
  };

  private async getCourses(): Promise<Course[]> {
    if (this.coursesCache) {
      return this.coursesCache;
    }

    if (this.coursesFetchPromise) {
      const response = await this.coursesFetchPromise;
      this.coursesCache = response.courses;
      return this.coursesCache;
    }

    this.coursesFetchPromise = firstValueFrom(this.api.fetchAllCourses());

    try {
      const response = await this.coursesFetchPromise;
      this.coursesCache = response.courses;
      this.coursesFetchPromise = null;
      return this.coursesCache;
    } catch (error) {
      this.coursesFetchPromise = null;
      throw error;
    }
  }

  private processCachedCourses(courses: Course[], search: string, page: number, pageSize: number, observer: any) {
    const filteredCourses = search ? courses.filter((course) => course.name.toLowerCase().includes(search.toLowerCase())) : courses;

    const startIndex = page * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredCourses.length);
    const paginatedCourses = filteredCourses.slice(startIndex, endIndex);

    const mappedData: SelectOption[] = paginatedCourses.map((course: Course) => ({
      id: course.courseId,
      title: course.name,
      selected: false
    }));

    observer.next({
      data: mappedData,
      total: filteredCourses.length
    });
    observer.complete();
  }

  async loadPromotionSegmentation(promotion: any) {
    // Suppress automatic resets while loading the promotion
    this.suppressSegmentationReset = true;
    try {
      // Load selected students
      if (promotion.studentSegmentations && promotion.studentSegmentations.length > 0) {
        const studentIds = promotion.studentSegmentations.map((s: any) => s.userId);
        await this.loadSelectedStudents(studentIds);
      }

      // Load selected courses
      if (promotion.courseSegmentations && promotion.courseSegmentations.length > 0) {
        await this.loadSelectedCourses(promotion.courseSegmentations);
      }

      // Force change detection to update the view
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading promotion segmentation:', error);
    } finally {
      // Re-activate resets after finishing loading
      this.suppressSegmentationReset = false;
      this.cdr.detectChanges();
    }
  }

  private async loadSelectedStudents(studentIds: string[]) {
    try {
      // Use the new endpoint to get students by IDs
      const response = await firstValueFrom(this.api.fetchStudentsByIds(studentIds));
      const students = response.students || response;

      if (students && Array.isArray(students)) {
        this.selectedStudents = students.map((student: ComboStudent) => ({
          id: student.id,
          title: student.fullName,
          subtitle: student.email,
          selected: true
        }));

        // Assign to the autocomplete component if available
        if (this.multiAutocomplete) {
          this.multiAutocomplete.selected = [...this.selectedStudents];
        }
      }
    } catch (error) {
      await this.loadSelectedStudentsLegacy(studentIds);
    }
  }

  private async loadSelectedStudentsLegacy(studentIds: string[]) {
    try {
      // Legacy implementation - get students individually
      const students: ComboStudent[] = [];

      // Use Promise.allSettled to make calls in parallel and handle individual errors
      const promises = studentIds.map(async (studentId) => {
        try {
          const response = await firstValueFrom(this.api.fetchReducedStudents({ search: studentId }));
          if (response && response.data && response.data.length > 0) {
            // Find the exact student by ID
            const student = response.data.find((s: ComboStudent) => s.id === studentId);
            if (student) {
              return student;
            }
          }
        } catch (error) {
          console.warn(`Error fetching student ${studentId}:`, error);
        }
        return null;
      });

      const results = await Promise.allSettled(promises);

      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          students.push(result.value);
        } else {
          console.warn(`Failed to load student with ID: ${studentIds[index]}`);
        }
      });

      this.selectedStudents = students.map((student: ComboStudent) => ({
        id: student.id,
        title: student.fullName,
        subtitle: student.email,
        selected: true
      }));

      // Assign to the autocomplete component if available
      if (this.multiAutocomplete) {
        this.multiAutocomplete.selected = [...this.selectedStudents];
      }
    } catch (error) {
      console.error('Error loading selected students (legacy method):', error);
    }
  }

  private async loadSelectedCourses(courseSegmentations: any[]) {
    try {
      // Separate enrolled and not enrolled courses
      const enrolledCourseIds = courseSegmentations.filter((cs) => cs.mustBeEnrolled).map((cs) => cs.courseId);

      const notEnrolledCourseIds = courseSegmentations.filter((cs) => !cs.mustBeEnrolled).map((cs) => cs.courseId);

      // Load all courses if not in cache
      if (!this.coursesCache) {
        const response = await firstValueFrom(this.api.fetchAllCourses());
        this.coursesCache = response.courses;
      }

      // Filter enrolled courses
      if (enrolledCourseIds.length > 0 && this.coursesCache) {
        this.selectedEnrolledCourses = this.coursesCache
          .filter((course) => enrolledCourseIds.includes(course.courseId))
          .map((course) => ({
            id: course.courseId,
            title: course.name,
            selected: true
          }));

        if (this.enrolledCoursesAutocomplete) {
          this.enrolledCoursesAutocomplete.selected = [...this.selectedEnrolledCourses];
        }
      }

      // Filter not enrolled courses
      if (notEnrolledCourseIds.length > 0 && this.coursesCache) {
        this.selectedNotEnrolledCourses = this.coursesCache
          .filter((course) => notEnrolledCourseIds.includes(course.courseId))
          .map((course) => ({
            id: course.courseId,
            title: course.name,
            selected: true
          }));

        if (this.notEnrolledCoursesAutocomplete) {
          this.notEnrolledCoursesAutocomplete.selected = [...this.selectedNotEnrolledCourses];
        }
      }
    } catch (error) {
      console.error('Error loading selected courses:', error);
    }
  }
}
